import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import clsx from "clsx";
import { Grid, Chip } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import "../../ResponsiveTable/_responsive.table.css";
import { lprSearchServiceInstance as lprSearchService } from "../../../services/LPRSearchService";
import { useStyles } from "./index.styles";

const PAGE_SIZE = 10;

/**
 * Small component that fetches the capture image blob and renders it as a thumbnail.
 * Uses apiClient to include auth headers. Only renders when isImageAvailable is true.
 */
const PlateImageThumbnail = ({ result, classes, onImageClick }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    lprSearchService
      .getCaptureImage(result.lprId)
      .then((response) => {
        if (!cancelled && response?.data) {
          objectUrl = URL.createObjectURL(response.data);
          setSrc(objectUrl);
        }
      })
      .catch(() => {
        // If fetch fails, thumbnail stays null and we show text fallback
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [result.lprId]);

  if (!src) {
    return (
      <span
        className={clsx(classes.clickablePlaceholder, "lpr-image-clickable")}
        onClick={() => onImageClick(result)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onImageClick(result);
        }}
        data-testid="lpr-image-thumbnail"
      >
        View Image
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={result.plateText || "Plate image"}
      className={classes.thumbnail}
      onClick={() => onImageClick(result)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onImageClick(result);
      }}
      data-testid="lpr-image-thumbnail"
    />
  );
};

const getImageCell = (result, classes, onImageClick) => {
  const { isImageAvailable, isNoPlateRead } = result;

  // isImageAvailable → show thumbnail (fetched lazily)
  if (isImageAvailable) {
    return (
      <PlateImageThumbnail
        result={result}
        classes={classes}
        onImageClick={onImageClick}
      />
    );
  }

  // isNoPlateRead → "No Plate" (clickable to view context image)
  if (isNoPlateRead) {
    return (
      <span
        className={clsx(classes.clickablePlaceholder, "lpr-image-clickable")}
        onClick={() => onImageClick(result)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onImageClick(result);
        }}
        data-testid="lpr-no-plate-placeholder"
      >
        No Plate
      </span>
    );
  }

  // Not available and not a no-plate → "No Image Available" (purged, not clickable)
  return (
    <span
      className={clsx(classes.nonClickablePlaceholder, "lpr-image-not-clickable")}
      data-testid="lpr-no-image-placeholder"
    >
      No Image Available
    </span>
  );
};

const formatDateTime = (dateTime, timezone) => {
  if (!dateTime || !timezone) return "";
  return moment.utc(dateTime).tz(timezone).format("MM/DD/YYYY h:mm A");
};

const formatConfidence = (confidence) => {
  if (confidence === null || confidence === undefined) return "";
  return `${Math.round(confidence)}%`;
};

const LPRSearchResults = ({
  results = [],
  currentPage = 1,
  totalCount = 0,
  onPageChange,
  onImageClick,
  hasSearched = false,
  isLoading = false,
  timezone,
}) => {
  const classes = useStyles();
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handlePageChange = (event, value) => {
    if (onPageChange) {
      onPageChange(value);
    }
  };

  // Show "No LPR reads found" when searched but no results
  if (hasSearched && results.length === 0 && !isLoading) {
    return (
      <Grid container className={classes.resultsContainer}>
        <Grid item xs={12}>
          <div
            className={clsx([
              "animate__animated",
              "animate__flipInX",
              classes.noResults,
            ])}
            data-testid="lpr-no-results-message"
          >
            <span className="no-results-text">No LPR reads found</span>
          </div>
        </Grid>
      </Grid>
    );
  }

  // Don't render anything if no search has been performed or no results
  if (!hasSearched || results.length === 0) {
    return null;
  }

  return (
    <Grid container className={classes.resultsContainer}>
      {/* Pagination - show only when totalCount > 10 */}
      {totalCount > PAGE_SIZE && (
        <Grid item xs={12} className={classes.pagination}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
            data-testid="lpr-pagination"
          />
        </Grid>
      )}

      {/* Results Table */}
      <Grid item xs={12}>
        <table
          className={clsx(classes.resultTable, "a1-responsive")}
          data-testid="lpr-results-table"
        >
          <thead className={clsx(classes.thHeader)}>
            <tr>
              <th className={classes.resultHeader}>Plate Text</th>
              <th className={classes.resultHeader}>Image</th>
              <th className={classes.resultHeader}>Confidence</th>
              <th className={classes.resultHeader}>Date/Time</th>
              <th className={classes.resultHeader}>Device Name</th>
              <th className={classes.resultHeader}>Type</th>
              <th className={classes.resultHeader}>Credential</th>
            </tr>
          </thead>
          <tbody>
            {results.map((result) => (
              <tr key={result.lprId} data-testid="lpr-result-row">
                <td data-column="Plate Text" className={classes.column}>
                  {result.plateText || ""}
                  {result.isNoPlateRead && (
                    <Chip label="No Plate" size="small" className={classes.noPlateChip} />
                  )}
                </td>
                <td data-column="Image" className={classes.column}>
                  {getImageCell(result, classes, onImageClick)}
                </td>
                <td data-column="Confidence" className={classes.column}>
                  {formatConfidence(result.confidence)}
                </td>
                <td data-column="Date/Time" className={classes.column}>
                  {formatDateTime(result.dateTimeCaptured, timezone)}
                </td>
                <td data-column="Device Name" className={classes.column}>
                  {result.deviceName || ""}
                </td>
                <td data-column="Type" className={classes.column}>
                  {result.credentialType || ""}
                </td>
                <td data-column="Credential" className={classes.column}>
                  {result.credentialReference || ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Grid>
    </Grid>
  );
};

export default LPRSearchResults;
